import React, { useMemo, useState } from "react"
import { Control } from "react-hook-form"
import * as Yup from "yup"
import {
  Text, Box,
} from "@chakra-ui/react"
import { addEventGroup, removeEventGroup, updateEventGroup } from "context/actions"
import { successToast } from "utilities/toasts"
import { deleteEventGroup, postEventGroup, putEventGroup } from "api/events"
import { useQueryClient } from "react-query"
import {
  EventGroup, EventUser, AccountUser, Notification, SubscriptionType,
} from "sharedTypes"
import UpgradeAlert from "components/elements/UpgradeAlert"
import useCurrentAccount from "services/useCurrentAccount"
import useCurrentPlanType from "utilities/useCurrentPlanType"
import TextField from "components/ReactHookForm/TextField"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import { eventAndAccountAdmins } from "utilities/objects"
import {
  NotificationList, toApiNotifications, type AlertRow,
} from "pages/EventShow/Guests/SelectedGuest/Show/CheckInAlert"
import Icon from "components/elements/Icon"
import ColorSwatchPicker from "components/ReactHookForm/ColorSelect/ColorSwatchPicker"
import ToggleButtonGroup, { ToggleButtonGroupItem } from "components/ReactHookForm/ToggleButtonGroup"
import { GUEST_LIST_ICONS, GUEST_LIST_ICON_NAMES, TagIcon } from "components/elements/icons"
import GuestListIcon, { DEFAULT_GUEST_LIST_COLOR, DEFAULT_GUEST_LIST_ICON } from "components/elements/GuestListIcon"

// Curated palette. Hex values chosen to read okay in both light and dark
// modes — see project doc "Color storage: hex vs named tokens". The gray
// swatch is the same value used as the renderer's fallback so backfilled,
// default, and uncustomized tags all look identical.
const TAG_COLORS = [
  DEFAULT_GUEST_LIST_COLOR, "#4399E1", "#49C5EA", "#41B2AC",
  "#48BB79", "#EDCA4B", "#EE8935", "#F36566", "#9F7BEA", "#EE65A6",
]

const DESCRIPTION = "Use Guest Lists to organize guests into categories for messaging, check‑in, reporting, and more throughout Diobox."

enum Screen {
  Main,
  CheckInAlert,
}

type Props = {
  eventId: string
  eventGroupId?: string
  defaultTitle?: string
  // Open directly on the Check-In Alert sub-screen — used by the bell
  // action in the sidebar group list so a click on "View alerts" lands
  // on the relevant section instead of the Main screen.
  openOnCheckInAlert?: boolean
  eventGroups: EventGroup[]
  users: EventUser[]
  teamMembers: AccountUser[]
  onHide: () => void
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required(),
})

const GuestListForm = ({
  eventId, eventGroupId, defaultTitle, openOnCheckInAlert,
  eventGroups, users, teamMembers, onHide,
}: Props) => {
  const queryClient = useQueryClient()
  const { subscription } = useCurrentAccount()
  const { onLargestPlan } = useCurrentPlanType()
  const [screen, setScreen] = useState<Screen>(
    openOnCheckInAlert ? Screen.CheckInAlert : Screen.Main,
  )

  const eventGroup = eventGroups.find(({ id }) => id.toString() === eventGroupId)

  let modalTitle
  let onSubmit
  let onDelete
  let initialValues

  const allUsers = useMemo(() => eventAndAccountAdmins(users, teamMembers), [users, teamMembers])
  const checkinEligibleUsers = useMemo(
    () => allUsers.filter((u) => u.user?.confirmed).map(({ user }) => user),
    [allUsers],
  )

  const buildNotifications = (existing: Notification[]) => checkinEligibleUsers.map((user) => {
    const match = existing.find((n) => n.userId === user.id)

    return { userId: user.id, email: match?.email ?? false, sms: match?.sms ?? false }
  })

  if (eventGroup) {
    modalTitle = "Edit Guest List"
    initialValues = {
      title: eventGroup.title,
      icon: eventGroup.icon ?? DEFAULT_GUEST_LIST_ICON,
      color: eventGroup.color ?? DEFAULT_GUEST_LIST_COLOR,
      notifications: buildNotifications(eventGroup.notifications || []),
    }
    onSubmit = ({
      title, icon, color, notifications,
    }) => putEventGroup(eventId, {
      id: eventGroup.id,
      title,
      icon,
      color,
      notifications: toApiNotifications(notifications) as any,
    }).then(({ data }) => {
      updateEventGroup(queryClient, eventId, data)
      successToast({ title: "Successfully updated Guest List" })
      onHide()
    })
    onDelete = () => {
      deleteEventGroup(eventId, eventGroup).then(() => {
        removeEventGroup(queryClient, eventId, eventGroup.id.toString())
        onHide()
      })
    }
  } else {
    modalTitle = "Add Guest List"
    initialValues = {
      title: defaultTitle ?? "",
      icon: DEFAULT_GUEST_LIST_ICON,
      color: DEFAULT_GUEST_LIST_COLOR,
      notifications: buildNotifications([]),
    }
    onSubmit = ({
      title, icon, color, notifications,
    }) => postEventGroup(eventId, {
      title,
      icon,
      color,
      notifications: toApiNotifications(notifications) as any,
    }).then(({ data }) => {
      addEventGroup(queryClient, eventId, data)
      successToast({ title: "Successfully created Guest List" })
      onHide()
    })
  }

  const isCheckInAlert = screen === Screen.CheckInAlert

  return (
    <ModalForm
      title={isCheckInAlert ? "Check-In Alert" : modalTitle}
      icon={isCheckInAlert ? "checkin-alert" : undefined}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onDanger={screen === Screen.Main ? onDelete : undefined}
      dangerProps={{ type: "Guest List", name: initialValues.title }}
      onClose={onHide}
      onBack={isCheckInAlert ? () => setScreen(Screen.Main) : undefined}
      size="xl"
      disableSubmitWhenNotDirty
    >
      {({ control, watch }) => {
        const color = (watch("color") as string) || DEFAULT_GUEST_LIST_COLOR
        const icon = (watch("icon") as string) || DEFAULT_GUEST_LIST_ICON
        const title = (watch("title") as string) || ""
        const notifications = (watch("notifications") as AlertRow[]) || []
        const activeCount = notifications.filter((r) => r.email || r.sms).length
        const SelectedIcon = GUEST_LIST_ICONS[icon] ?? TagIcon

        // Both screens stay mounted and visibility is toggled with `display`,
        // so local component state and scroll position survive back-and-forth
        // navigation. (Equivalent to React 19's `<Activity>` which isn't
        // available on 18.3.)
        return (
          <>
            <Box display={screen === Screen.CheckInAlert ? "block" : "none"}>
              <Text mb={2}>
                Notify team members when anyone on this Guest List checks in:
              </Text>
              <Box borderWidth={1} borderRadius="md" borderColor="gray.200" bg="gray.50" py={3} px={4} display="flex" alignItems="center" gap={4} mb={6}>
                <Box
                  borderWidth={1}
                  borderRadius="md"
                  borderColor={color}
                  bg={`color-mix(in srgb, ${color} 8%, white)`}
                  p={2}
                  flexShrink={0}
                >
                  <GuestListIcon icon={icon} color={color} boxSize={6} />
                </Box>
                <Text fontWeight="semibold" fontSize="lg">{title || "Guest List"}</Text>
              </Box>
              {subscription && (
                <NotificationList
                  control={control}
                  users={checkinEligibleUsers}
                  smsDisabled={!onLargestPlan}
                />
              )}
              {!subscription && (
                <Box mt={4}>
                  <UpgradeAlert text="Check-In Alerts are available with a paid subscription." />
                </Box>
              )}
              {subscription && !onLargestPlan && (
                <Box mt={4}>
                  <UpgradeAlert
                    text="SMS Check-In Alerts are available with an Enterprise subscription."
                    defaultSelectedPlan={SubscriptionType.Enterprise}
                  />
                </Box>
              )}
            </Box>

            <Box display={screen === Screen.Main ? "block" : "none"}>
              <Text mb={6}>{DESCRIPTION}</Text>

              <Box mb={8}>
                <TextField
                  name="title"
                  control={control}
                  variant="flushed"
                  size="xl"
                  placeholder="Guest List Name"
                  ariaLabel="Guest List Name"
                  h={12}
                  leftElement={<SelectedIcon boxSize={6} color={color} />}
                  mb={0}
                />
              </Box>

              <Box mb={8}>
                <Text fontSize="md" fontWeight="semibold" mb={3}>Color</Text>
                <ColorSwatchPicker name="color" control={control} swatches={TAG_COLORS} />
              </Box>

              <Box mb={8}>
                <Text fontSize="md" fontWeight="semibold" mb={3}>Icon</Text>
                <IconGrid control={control} color={color} />
              </Box>

              <CheckInAlertRow
                count={activeCount}
                onClick={() => setScreen(Screen.CheckInAlert)}
              />
            </Box>
          </>
        )
      }}
    </ModalForm>
  )
}

type CheckInAlertRowProps = {
  count: number
  onClick: () => void
}

const CheckInAlertRow = ({ count, onClick }: CheckInAlertRowProps) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    w="full"
    textAlign="left"
    borderWidth={1}
    borderRadius="md"
    borderColor="gray.200"
    bg="gray.50"
    py={3}
    px={4}
    display="flex"
    alignItems="center"
    gap={3}
    _hover={{ bg: "gray.100" }}
  >
    <Icon icon="checkin-alert" size={6} color="gray.600" />
    <Box flexGrow={1}>
      <Text fontWeight="semibold">Check-In Alert</Text>
      <Text fontSize="sm" color="gray.700">
        {count === 0
          ? "No one notified on check-in"
          : `${count} ${count === 1 ? "person" : "people"} notified on check-in`}
      </Text>
    </Box>
    <Icon icon="next-arrow" size={4} color="gray.500" />
  </Box>
)

// Extracted so the items array memoizes on `color` only — the parent's
// render-frequent `watch()` updates (e.g. typing in the title) don't
// rebuild the 24-icon button closures unless the picked color changed.
const IconGrid = React.memo(({ control, color }: { control: Control<any>; color: string }) => {
  const items = useMemo(
    () => GUEST_LIST_ICON_NAMES.map((iconName) => {
      const IconComponent = GUEST_LIST_ICONS[iconName]

      return {
        value: iconName,
        button: (selected: boolean, onClick: () => void) => (
          <ToggleButtonGroupItem
            selected={selected}
            onClick={onClick}
            tintColor={color}
            title={iconName}
            p={2}
            h="auto"
            minH={0}
            sx={{ aspectRatio: "1 / 1" }}
          >
            <IconComponent boxSize={6} color={color} />
          </ToggleButtonGroupItem>
        ),
      }
    }),
    [color],
  )

  return (
    <ToggleButtonGroup name="icon" control={control} columns={8} spacing={2}>
      {items}
    </ToggleButtonGroup>
  )
})

export default GuestListForm
